import React, { useEffect, useState } from "react";
import { supabase } from "../supabaseClient";
import { getAllBets } from "../Data";
import { CheckBetResults, acceptBets, deleteBets, archiveBet } from "../Components";
import { useTheme } from "../Components/theme/ThemeContext.jsx";
import SeasonalCardToggle from "../Components/theme/SeasonalCardToggle.jsx";
import 'bootstrap/dist/css/bootstrap.min.css';

const Loader = () => (
  <div className="spinner-border text-primary" role="status">
    <span className="sr-only">Loading...</span>
  </div>
);

const E_GIFT_CARD_OPTIONS = [
  { vendor: "Tim Hortons", url: "https://www.timhortons.ca/tims-gift-card" },
  { vendor: "Amazon", url: "https://www.amazon.ca/s?k=amazon+e-+gift+card&gad_source=1&hvadid=667162485047&hvdev=c&hvexpln=0&hvlocphy=9000000&hvnetw=g&hvocijid=13274356268439495166--&hvqmt=e&hvrand=13274356268439495166&hvtargid=kwd-295527406049&hydadcr=23336_13656850&mcid=22d6d07bdfff337c8fafd82b73190a55&tag=googcana-20&ref=pd_sl_2jc7d2zncj_e" },
  { vendor: "Starbucks", url: "https://www.starbucks.com/gift" },
];

const MyBets = () => {
  const { activeCardTheme, seasonalCardsEnabled: showSeasonalBetCards } = useTheme();
  const isSeasonalCardSeason = activeCardTheme === "halloween" || activeCardTheme === "christmas";
  const [activeBetArr, setActiveBetArr] = useState([]);
  const [settledBetArr, setSettledBetArr] = useState([]);
  const [pendingBetArr, setPendingBetArr] = useState([]);
  const [betHistory, setBetHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [view, setView] = useState(''); // State to toggle between views

  const [showGiftModal, setShowGiftModal] = useState(false);
  const [giftBetId, setGiftBetId] = useState(null);
  const [giftRecipient, setGiftRecipient] = useState("");
  const [giftRecipientPublicIds, setGiftRecipientPublicIds] = useState([]);
  const [giftAmount, setGiftAmount] = useState("");
  const [giftVendor, setGiftVendor] = useState(E_GIFT_CARD_OPTIONS[0].vendor);
  const [giftCode, setGiftCode] = useState("");
  const [giftPurchaseUrl, setGiftPurchaseUrl] = useState("");
  const [giftSending, setGiftSending] = useState(false);

  // Modal handlers
  const handleOpenGiftModal = (betId, recipientLabel, recipientPublicId, wager) => {
    setGiftBetId(betId);
    setGiftRecipient(recipientLabel || "");
    setGiftRecipientPublicIds(recipientPublicId ? [recipientPublicId] : []);
    setGiftAmount("");
    const defaultGiftOption = E_GIFT_CARD_OPTIONS[0];
    setGiftVendor(defaultGiftOption.vendor);
    setGiftCode("");
    setGiftPurchaseUrl(defaultGiftOption.url);
    setShowGiftModal(true);
  };

  const handleCloseGiftModal = () => {
    setShowGiftModal(false);
    setGiftCode("");
    setGiftPurchaseUrl("");
    setGiftRecipientPublicIds([]);
    setGiftSending(false);
  };

  const handleCreateGiftCardLink = () => {
    const selectedOption = E_GIFT_CARD_OPTIONS.find((option) => option.vendor === giftVendor);

    if (!selectedOption?.url) {
      alert("Purchase page unavailable for this brand.");
      return;
    }

    setGiftPurchaseUrl(selectedOption.url);
    window.open(selectedOption.url, "_blank", "noopener,noreferrer");
  };

  const handleSubmitGiftCard = async () => {
    if (!currentUser?.id) {
      alert("Could not verify current user.");
      return;
    }

    const normalizedCode = giftCode.trim();
    if (!normalizedCode) {
      alert("Please enter the redemption details from your e-gift purchase.");
      return;
    }

    if (giftRecipientPublicIds.length === 0) {
      alert("No recipient found for this bet.");
      return;
    }

    try {
      setGiftSending(true);

      const { data: recipients, error: recipientsError } = await supabase
        .from("users")
        .select("id, username, public_user_id")
        .in("public_user_id", giftRecipientPublicIds);

      if (recipientsError) {
        throw recipientsError;
      }

      if (!recipients || recipients.length === 0) {
        alert("Could not find recipient account.");
        setGiftSending(false);
        return;
      }

      const rows = recipients.map((recipient) => ({
        bet_id: String(giftBetId),
        sender_user_id: currentUser.id,
        recipient_user_id: recipient.id,
        gift_vendor: giftVendor,
        gift_amount: giftAmount || null,
        gift_code: normalizedCode,
        purchase_url: giftPurchaseUrl || null,
        message: `${currentUser.username} sent ${giftVendor} e-gift redemption details for bet #${giftBetId}.`,
      }));

      const { error: insertError } = await supabase
        .from("gift_card_notifications")
        .insert(rows);

      if (insertError) {
        throw insertError;
      }

      alert("E-gift details sent. The winner can now see them in Notifications.");
      handleCloseGiftModal();
    } catch (error) {
      console.error("Failed to send gift card notification:", error);
      alert("Failed to send e-gift details.");
    } finally {
      setGiftSending(false);
    }
  };

  // Create Bet Cards
  const createBetCard = (
    betId,
    game_ID,
    gameTitle,
    friends,
    homeLogo,
    awayLogo,
    wager,
    betDes,
    betcreator,
    creatorId,
    friendId,
    loggedInUserPublicId,
    loggedInUserUsername,
    betStatus,
    result
  ) => {
    const betDescriptions = betDes
      ? Object.entries(betDes)
          .filter(([key, value]) => value)
          .map(([key, value]) => key)
      : [];

    return (
      <div key={betId} className="col-12 col-sm-6 col-xl-4 mb-3">
        <article className={`my-bets__card card h-100 ${showSeasonalBetCards && isSeasonalCardSeason ? "seasonal-sport-card" : ""}`}>
        {showSeasonalBetCards && activeCardTheme === "halloween" && (
          <span className="seasonal-sport-card__spider" aria-hidden="true">🕷️</span>
        )}
        {showSeasonalBetCards && activeCardTheme === "christmas" && (
          <span className="seasonal-sport-card__lights" aria-hidden="true">
            {Array.from({ length: 16 }, (_, index) => <i key={index} />)}
          </span>
        )}
        <div className="card-body">
          {betStatus === "settled" && (
            <div className="position-absolute top-0 end-0 me-1 mt-1">
              <button
                type="button"
                className="btn-close"
                aria-label="Archive settled bet"
                onClick={() => archiveBet(betId, fetchBetData)}
              ></button>
            </div>
          )}
          <h5 className="card-title">{gameTitle}</h5>
          <div className="row">
            <div className="col">
              <img
                src={homeLogo}
                className="my-bets__team-logo"
                alt="Home Team"
              ></img>
            </div>
            <div className="col">
              <img
                src={awayLogo}
                alt="Away Team"
                className="my-bets__team-logo"
              />
            </div>
          </div>
          <p>
            {betcreator} bets{" "}
            {Array.isArray(friends) ? friends.join(", ") : friends}: {wager}
          </p>
          {betDescriptions.map((desc, index) => (
            <p key={index}>{desc}</p>
          ))}
          <p>Result: {result}</p>
          <div className="row">
            {betStatus === "pending" && betcreator === loggedInUserUsername ? (
              <p>
                Awaiting {Array.isArray(friends) ? friends.join(", ") : friends}{" "}
                Confirmation
              </p>
            ) : null}
            {betStatus === "pending" && betcreator !== loggedInUserUsername ? (
              <>
                <div className="col">
                  <button
                    className="btn btn-primary w-100 mb-1"
                    onClick={() => acceptBets(betId, fetchBetData)}
                  >
                    Accept
                  </button>
                </div>
                <div className="col">
                  <button
                    className="btn btn-secondary w-100"
                    onClick={() => deleteBets(betId, fetchBetData)}
                  >
                    Decline
                  </button>
                </div>
              </>
            ) : (
              <>
                {betcreator !== loggedInUserUsername || betStatus !== "pending" ? (
                  betStatus === "settled" ? (
                    <div className="d-grid gap-3 mt-3">
                      <button
                        className="btn btn-success"
                        onClick={() => {
                          const isCreator = creatorId === loggedInUserPublicId;
                          const recipientPublicId = isCreator ? friendId : creatorId;
                          const recipientLabel = isCreator
                            ? (Array.isArray(friends) ? friends.join(", ") : friends)
                            : betcreator;

                          handleOpenGiftModal(betId, recipientLabel, recipientPublicId, wager);
                        }}
                      >
                        Send Gift Card
                      </button>
                    </div>
                  ) : (
                    <div className="row">
                      <div className="col-12">
                        <button
                          onClick={() => {
                            CheckBetResults(betId, fetchBetData);
                          }}
                          className="btn btn-primary w-100"
                        >
                          Results
                        </button>
                      </div>
                    </div>
                  )
                ) : (
                  <div className="col-12">
                    <button
                      onClick={() => {
                        deleteBets(betId, fetchBetData);
                      }}
                      className="btn btn-outline-danger w-100"
                    >
                      Withdraw Bet
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
        </article>
      </div>
    );
  };

  const renderBetCards = (bets) => bets.map((bet) => createBetCard(
    bet.betid,
    bet.gameid,
    bet.gametitle,
    bet.friends || [],
    bet.awaylogo,
    bet.homelogo,
    bet.wager,
    bet.betdescription || {},
    bet.betcreator,
    bet.creator_id,
    bet.friend_id,
    currentUser?.public_user_id,
    currentUser?.username,
    bet.betstatus,
    bet.result
  ));

  const fetchBetData = async () => {
    setLoading(true);
    const allBets = await getAllBets();

    const { data: sessionData, error: sessionError } =
      await supabase.auth.getSession();
    if (sessionError) {
      console.error("Error fetching session:", sessionError);
      setLoading(false);
      return;
    }

    if (sessionData && sessionData.session) {
      const user = sessionData.session.user;
      const { data: userData, error: userError } = await supabase
        .from("users")
        .select("*")
        .eq("public_user_id", user.id)
        .single();

      if (userError) {
        console.error("Error fetching user data:", userError);
        setLoading(false);
        return;
      }

      setCurrentUser(userData);

      let pendingBets = allBets.filter(
        (bet) =>
          (bet.creator_id === userData.public_user_id ||
            bet.friend_id === userData.public_user_id) &&
          bet.betstatus === "pending"
      );

      let activeBets = allBets.filter(
        (bet) =>
          (bet.creator_id === userData.public_user_id ||
            bet.friend_id === userData.public_user_id) &&
          bet.betstatus === "active"
      );

      let settledBets = allBets.filter(
        (bet) =>
          (bet.creator_id === userData.public_user_id ||
            bet.friend_id === userData.public_user_id) &&
          bet.betstatus === "settled"
      );

      const historyBets = allBets
        .filter((bet) =>
          bet.creator_id === userData.public_user_id ||
          bet.friend_id === userData.public_user_id
        )
        .sort((first, second) => {
          const firstDate = new Date(first.withdrawn_at || Number(first.betid) || 0).getTime();
          const secondDate = new Date(second.withdrawn_at || Number(second.betid) || 0).getTime();
          return secondDate - firstDate;
        });

      setPendingBetArr(pendingBets);
      setActiveBetArr(activeBets);
      setSettledBetArr(settledBets);
      setBetHistory(historyBets);
      if (pendingBets.length > 0) {
        setView("pending");
      } else if (activeBets.length > 0) {
        setView("active");
      } else if (settledBets.length>0) {
        setView("settled");
      } else if (historyBets.length > 0) {
        setView("history");
      } else {
        setView("empty");
      }
    }

    setLoading(false);
  };

  useEffect(() => {
    fetchBetData();
  }, []);

  return (
    <main className="fb-page my-bets-page">
      <div className="my-bets-page__panel">
        {isSeasonalCardSeason && (
          <SeasonalCardToggle className="my-bets-page__seasonal-toggle-row" />
        )}
        <header className="fb-page__header text-center">
          <p className="fb-page__eyebrow">Your wagers</p>
          <h1 className="my-bets-page__title">My Bets</h1>
          <p className="my-bets-page__intro">Keep up with every matchup and result.</p>
        </header>

        {/* Toggle between current bets and full history */}
        <div className="my-bets-page__tabs-row">
          <div className="btn-group mb-3 my-bets-page__tabs" role="group" aria-label="Bet View Toggle">
          <button
            type="button"
            className={`btn ${view === 'pending' ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => setView('pending')}
          >
          Pending  
          </button>
          <button
            type="button"
            className={`btn ${view === 'active' ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => setView('active')}
          >
            Active
          </button>
          <button
            type="button"
            className={`btn ${view === 'settled' ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => setView('settled')}
          >
            Settled
          </button>
          <button
            type="button"
            className={`btn ${view === 'history' ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => setView('history')}
          >
            History
          </button>
          </div>
        </div>

        {/* Conditionally render based on selected view */}
        {view === "pending" && (
          <>
            <div className="row justify-content-center">
              {loading ? (
                <Loader />
              ) : pendingBetArr.length === 0 ? (
                "No Pending Bets"
              ) : (
                renderBetCards(pendingBetArr)
              )}
            </div>
          </>
        )}

        {view === "active" && (
          <>
            <div className="row justify-content-center">
              {loading ? (
                <Loader />
              ) : activeBetArr.length === 0 ? (
                "No Active Bets"
              ) : (
                renderBetCards(activeBetArr)
              )}
            </div>
          </>
        )}

        {view === "settled" && (
          <>
            <div className="row justify-content-center">
              {loading ? (
                <Loader />
              ) : settledBetArr.length === 0 ? (
                "No Settled Bets"
              ) : (
                renderBetCards(settledBetArr)
              )}
            </div>
          </>
        )}
        {view === "history" && (
          <>
            {loading ? (
              <Loader />
            ) : betHistory.length === 0 ? (
              <p className="my-bets-page__empty">No bets in your history yet.</p>
            ) : (
              <div className="table-responsive">
                <table className="table my-bets__history-table align-middle text-start">
                  <thead>
                    <tr>
                      <th scope="col">Game</th>
                      <th scope="col">Players</th>
                      <th scope="col">Wager</th>
                      <th scope="col">Status / Result</th>
                    </tr>
                  </thead>
                  <tbody>
                    {betHistory.map((bet) => {
                      const withdrawn = bet.betstatus === "withdrawn";
                      const statusOrResult = withdrawn
                        ? `Withdrawn by ${bet.withdrawn_by_name || (bet.withdrawn_by === currentUser?.public_user_id ? currentUser.username : "another user")}`
                        : bet.result && bet.result !== "Waiting"
                          ? `${bet.result}${bet.betstatus === "archived" ? " (archived)" : ""}`
                          : `${bet.betstatus?.charAt(0).toUpperCase()}${bet.betstatus?.slice(1) || "Unknown"}`;

                      return (
                        <tr key={bet.betid}>
                          <td>{bet.gametitle || "Game unavailable"}</td>
                          <td>{bet.betcreator || "Unknown"} vs. {bet.friends || "Unknown"}</td>
                          <td>{bet.wager || "—"}</td>
                          <td>{statusOrResult}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
        {view==="empty" &&(
          <>
          <h2 className="my-bets-page__empty">You have no bets.</h2></>
        )}

        {showGiftModal && (
          <div className="modal d-block" tabIndex="-1" role="dialog">
            <div className="modal-dialog" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Settle Bet With E-Gift</h5>
                  <button
                    type="button"
                    className="btn-close"
                    aria-label="Close"
                    onClick={handleCloseGiftModal}
                  ></button>
                </div>
                <div className="modal-body text-start">
                  <p className="mb-2">
                    Recipient: <strong>{giftRecipient || "Unknown"}</strong>
                  </p>
                  <p className="mb-2">
                    Bet ID: <strong>{giftBetId}</strong>
                  </p>

                  <div className="mb-2">
                    <label className="form-label">Gift Brand</label>
                    <select
                      className="form-select"
                      value={giftVendor}
                      onChange={(event) => {
                        const selectedVendor = event.target.value;
                        setGiftVendor(selectedVendor);

                        const selectedOption = E_GIFT_CARD_OPTIONS.find(
                          (option) => option.vendor === selectedVendor
                        );
                        setGiftPurchaseUrl(selectedOption?.url || "");
                      }}
                    >
                      {E_GIFT_CARD_OPTIONS.map((option) => (
                        <option key={option.vendor} value={option.vendor}>
                          {option.vendor}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="mb-2">
                    <label className="form-label">Amount (Optional)</label>
                    <input
                      className="form-control"
                      value={giftAmount}
                      onChange={(event) => setGiftAmount(event.target.value)}
                      placeholder="ex: 20"
                    />
                  </div>

                  <div className="mb-2">
                    <button
                      type="button"
                      className="btn btn-outline-primary w-100"
                      onClick={handleCreateGiftCardLink}
                    >
                      Open {giftVendor} Purchase Page
                    </button>
                    {giftPurchaseUrl && (
                      <small className="text-muted d-block mt-1">
                        Selected purchase page is ready.
                      </small>
                    )}
                    <small className="text-muted d-block mt-1">
                      Complete checkout, then add the redemption details below.
                    </small>
                  </div>

                  <div className="mb-2">
                    <label className="form-label">Redemption Details</label>
                    <input
                      className="form-control"
                      value={giftCode}
                      onChange={(event) => setGiftCode(event.target.value)}
                      placeholder="Enter code or claim instructions"
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={handleCloseGiftModal}
                    disabled={giftSending}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-success"
                    onClick={handleSubmitGiftCard}
                    disabled={giftSending || !giftCode.trim()}
                  >
                    {giftSending ? "Sending..." : "Send to Winner"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </main>
  );
};

export { MyBets, Loader };
